import { CdkPortalOutlet, Portal, TemplatePortal } from '@angular/cdk/portal';
import { NgClass } from '@angular/common';
import {
    booleanAttribute,
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    ElementRef,
    inject,
    input,
    OnInit,
    output,
    signal,
    TemplateRef,
    viewChildren,
    ViewContainerRef,
    ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { debounceTime } from 'rxjs';

export interface M040C140Tabs {
    value: string;
    label: string;
    content?: TemplateRef<unknown>;
    portal?: Portal<unknown>;
    visible?: boolean;
}

@Component({
    selector: 'ui-m040-c140-tabs',
    standalone: true,
    imports: [FormsModule, ReactiveFormsModule, NgClass, CdkPortalOutlet],
    templateUrl: './m040-c140-tabs.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class M040C140TabsComponent implements OnInit {
    _viewContainerRef = inject(ViewContainerRef);
    destroyRef = inject(DestroyRef);
    control = input.required<FormControl<string>>();
    options = input.required<M040C140Tabs[]>();
    disableOverflow = input(true, { transform: booleanAttribute });
    dynamicHeight = input(false, { transform: booleanAttribute });
    small = input(false, { transform: booleanAttribute });
    styleTabContainer = signal('transform: translateX(0); width: 200%;'); // Default Value
    styleTabContent = signal('width: 50%;');
    currentIndex = signal(0);
    tabChangeEvent = output<string>();

    tabs = viewChildren('tabs', { read: ElementRef });

    optionsMapped = computed(() => {
        return this.options().map((o) => {
            let template = o.portal;

            if (!template && o.content) {
                template = new TemplatePortal(o.content, this._viewContainerRef);
            }

            return {
                ...o,
                template,
            };
        });
    });

    ngOnInit() {
        if (this.control().value) {
            const index = this.options().findIndex((option) => option.value === this.control().value);
            this.tabChange(Math.max(index, 0));
        } else {
            this.tabChange(0);
            this.control().setValue(this.options()[0].value);
        }

        this.control()
            .valueChanges.pipe(debounceTime(150), takeUntilDestroyed(this.destroyRef))
            .subscribe((value) => {
                const index = this.options().findIndex((option) => option.value === value);
                if (this.currentIndex() !== index) {
                    this.tabChange(index);
                }
            });
    }

    tabChange(index: number): void {
        this.control().setValue(this.options()[index].value);
        this.tabChangeEvent.emit(this.options()[index].value);

        const percent = 100 / this.options().length;
        const translateX = percent * index;
        const width = this.options().length * 100;
        const style = `transform: translateX(-${translateX}%); width: ${width}%`;

        this.styleTabContainer.set(`${style}; height: auto`);
        if (this.dynamicHeight()) {
            let newHeight = 0;
            for (const { i, tab } of this.tabs().map((tab, i) => ({ i, tab }))) {
                if (index === i) {
                    newHeight = tab.nativeElement.firstChild.offsetHeight;
                }
            }
            this.styleTabContainer.set(`${style}; height: auto; min-height: ${newHeight}px`);
        }
        this.currentIndex.set(index);

        queueMicrotask(() => {
            const panel = this.tabs()?.[index]?.nativeElement;
            if (!panel) {
                return;
            }
            panel.focus({ preventScroll: true });
        });
    }
}
